import React, { useEffect, useState } from 'react';

const card = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 18 };
const h3 = { fontSize: 13, fontWeight: 700, color: '#94A3B8', margin: '0 0 14px', letterSpacing: 0.4 };
const muted = { color: '#64748B', fontSize: 13 };
const TYPE_COLORS = { join: '#38BDF8', progress: '#34D399', post: '#F59E0B', comment: '#A78BFA', like: '#F472B6' };
const TYPES = Object.keys(TYPE_COLORS);
const shortDate = (iso) => { const [, m, d] = iso.split('-'); return `${Number(d)}/${Number(m)}`; };

const Empty = ({ testId }) => <div data-testid={testId} style={muted}>No activity in this period</div>;

const ActiveUsersChart = ({ rows }) => {
  const max = Math.max(0, ...rows.map(r => r.count));
  if (max === 0) return <Empty testId="analytics-active-empty" />;
  return (
    <div data-testid="analytics-active-chart" style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 140, overflowX: 'auto' }}>
      {rows.map(r => (
        <div key={r.date} title={`${r.date}: ${r.count}`} style={{ flex: '1 0 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
          <div style={{ fontSize: 10, color: '#CBD5E1' }}>{r.count || ''}</div>
          <div style={{ width: '100%', maxWidth: 28, height: `${(r.count / max) * 100}%`, minHeight: r.count ? 3 : 0, background: '#DC2626', borderRadius: 4 }} />
          <div style={{ fontSize: 9, color: '#64748B', whiteSpace: 'nowrap' }}>{shortDate(r.date)}</div>
        </div>
      ))}
    </div>
  );
};

const ActivityChart = ({ rows }) => {
  const totals = rows.map(r => TYPES.reduce((s, t) => s + r[t], 0));
  const max = Math.max(0, ...totals);
  if (max === 0) return <Empty testId="analytics-activity-empty" />;
  return (
    <>
      <div data-testid="analytics-activity-chart" style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 140, overflowX: 'auto' }}>
        {rows.map((r, i) => (
          <div key={r.date} title={`${r.date}: ${TYPES.map(t => `${t} ${r[t]}`).join(', ')}`} style={{ flex: '1 0 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
            <div style={{ fontSize: 10, color: '#CBD5E1' }}>{totals[i] || ''}</div>
            <div style={{ width: '100%', maxWidth: 28, height: `${(totals[i] / max) * 100}%`, display: 'flex', flexDirection: 'column-reverse', borderRadius: 4, overflow: 'hidden' }}>
              {TYPES.map(t => r[t] > 0 && <div key={t} style={{ flex: r[t], background: TYPE_COLORS[t] }} />)}
            </div>
            <div style={{ fontSize: 9, color: '#64748B', whiteSpace: 'nowrap' }}>{shortDate(r.date)}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10 }}>
        {TYPES.map(t => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#CBD5E1' }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: TYPE_COLORS[t] }} />{t} ({rows.reduce((s, r) => s + r[t], 0)})
          </span>
        ))}
      </div>
    </>
  );
};

const Funnel = ({ steps }) => {
  const top = steps[0]?.count || 0;
  if (!top) return <div style={muted}>No accounts in this organisation.</div>;
  return (
    <div data-testid="analytics-funnel" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {steps.map(s => (
        <div key={s.step}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#E2E8F0', marginBottom: 4 }}>
            <span>{s.step}</span><span>{s.count} · {Math.round((s.count / top) * 100)}%</span>
          </div>
          <div style={{ height: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 5 }}>
            <div style={{ width: `${(s.count / top) * 100}%`, height: '100%', background: '#DC2626', borderRadius: 5 }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const Stat = ({ label, value, note, testId }) => (
  <div style={{ ...card, padding: 16 }} data-testid={testId}>
    <div style={{ fontSize: 11, color: '#64748B', fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>{label.toUpperCase()}</div>
    <div style={{ fontSize: 26, fontWeight: 700, color: '#F1F5F9' }}>{value}</div>
    {note && <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>{note}</div>}
  </div>
);

export default function AnalyticsPanel({ orgs, authedRequest }) {
  const [orgId, setOrgId] = useState(orgs[0]?.id || '');
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (!orgId && orgs[0]) setOrgId(orgs[0].id); }, [orgs, orgId]);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    authedRequest('get', `/superadmin/analytics?org_id=${encodeURIComponent(orgId)}&days=${days}`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) { setData(null); setError(e.response?.data?.detail || 'Could not load analytics'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, days, authedRequest]);

  const btn = (active) => ({ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.12)', background: active ? '#DC2626' : 'transparent', color: active ? '#FFF' : '#CBD5E1' });

  return (
    <div data-testid="analytics-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} data-testid="analytics-org-select"
          style={{ flex: '1 1 220px', maxWidth: 360, padding: '9px 12px', borderRadius: 8, background: '#1E293B', color: '#F1F5F9', border: '1px solid rgba(255,255,255,0.12)', fontSize: 14 }}>
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6 }}>
          {[7, 30].map(d => <button key={d} onClick={() => setDays(d)} data-testid={`analytics-days-${d}`} style={btn(days === d)}>{d} days</button>)}
        </div>
      </div>
      <div data-testid="analytics-note" style={{ fontSize: 12, color: '#FCD34D', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '8px 12px' }}>
        Login history starts from the day login tracking was added. Failures are not tracked yet.
      </div>

      {loading && <div style={muted}>Loading…</div>}
      {error && <div data-testid="analytics-error" style={{ color: '#FCA5A5', fontSize: 13 }}>{error}</div>}

      {data && !loading && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <Stat label="Accounts" value={data.summary.accounts} testId="analytics-accounts" />
            <Stat label="Onboarding done" value={data.summary.onboarding_completed} testId="analytics-onboarded" />
            <Stat label="Logged in (tracked)" value={data.summary.accounts_with_login} note="Counted from when tracking started" testId="analytics-logged-in" />
            <Stat label={`Active users (${days}d)`} value={data.active_users_total} note="Distinct users with activity or a login" testId="analytics-active-total" />
          </div>
          <section style={card}><h3 style={h3}>ACTIVE USERS PER DAY (INDIA TIME)</h3><ActiveUsersChart rows={data.active_users_per_day} /></section>
          <section style={card}><h3 style={h3}>ACTIVITY BY TYPE PER DAY</h3><ActivityChart rows={data.activity_per_day} /></section>
          <section style={card}><h3 style={h3}>FUNNEL (ALL TIME, DISTINCT USERS)</h3><Funnel steps={data.funnel} /></section>
          <section style={card}>
            <h3 style={h3}>NO ACTIVITY YET AND ONBOARDING NOT DONE ({data.no_activity_not_onboarded.length})</h3>
            {data.no_activity_not_onboarded.length === 0 ? <div style={muted}>Everyone has started.</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table data-testid="analytics-inactive-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ color: '#64748B', textAlign: 'left' }}><th style={{ padding: '6px 8px' }}>Name</th><th style={{ padding: '6px 8px' }}>Email</th><th style={{ padding: '6px 8px' }}>Role</th></tr></thead>
                  <tbody>
                    {data.no_activity_not_onboarded.map(u => (
                      <tr key={u.email} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <td style={{ padding: '6px 8px', color: '#F1F5F9' }}>{u.name}</td>
                        <td style={{ padding: '6px 8px', color: '#CBD5E1', wordBreak: 'break-all' }}>{u.email}</td>
                        <td style={{ padding: '6px 8px', color: '#94A3B8' }}>{u.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
